'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { IEditUser, useEditUserApi, userDataSchema, useSession } from '@repo/sdk'
import { Button, FormField, Input, Spinner } from '@repo/ui'
import { toast } from '@repo/ui'
import 'react-phone-input-2/lib/style.css'
import { Form, FormControl, FormItem, FormLabel, FormMessage } from '@repo/ui/components'
import { cn } from '@repo/ui/lib'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import ReactPhoneInput, { PhoneInputProps } from 'react-phone-input-2'
import { z } from 'zod'

export const EditUserData = () => {
  const session = useSession()

  const defaultValues = session?.data?.user && {
    ...session.data.user,
    id: z.coerce.number().parse(session.data.user.id),
    phone: session.data?.user?.phone && String(session.data?.user?.phone),
  }
  const { editUserShipper } = useEditUserApi()

  const form = useForm({
    defaultValues: defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(userDataSchema),
    reValidateMode: 'onSubmit',
  })

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues)
    }
  }, [session?.data?.user])

  const { formState, handleSubmit, register, reset } = form

  const onSubmit: SubmitHandler<IEditUser> = async data => {
    // @ts-ignore
    delete data.role

    try {
      const response = await editUserShipper({
        ...data,
        phone: data.phone === '' ? null : data.phone,
      })

      if (response) {
        await session.update(response)
      }
      toast.success('User updated')
    } catch (err: any) {
      toast.error(err.message)
    }
  }

  return (
    <div>
      <div className={'border-b border-b-gray-200 pb-2 md:pb-4'}>
        <p className={'text-lg font-medium md:text-2xl'}>Your Account Information</p>
      </div>
      {defaultValues ? (
        <>
          <p className={'mt-4 text-lg font-medium md:mt-8 md:text-xl'}>General info</p>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={'grid grid-cols-1 gap-2 py-4 xl:grid-cols-2 xl:gap-6'}>
                <FormField
                  control={form.control}
                  name={'name'}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="First name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'last_name'}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Last name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'email'}
                  render={() => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...register('email')} placeholder="Email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'phone'}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <PhoneInput {...field} placeholder="Phone number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className={'flex justify-end'}>
                <Button className={'font-semibold'} disabled={formState.isSubmitting} type="submit">
                  Save {formState.isSubmitting && <Spinner />}
                </Button>
              </div>
            </form>
          </Form>
        </>
      ) : (
        <div className={'flex h-40 items-center justify-center'}>
          <Spinner className="h-24 w-24 text-brand" />
        </div>
      )}
    </div>
  )
}

const PhoneInput = (props: PhoneInputProps) => {
  return (
    <ReactPhoneInput
      buttonClass={cn(
        '!border-input !rounded-l-lg !bg-background dark:!text-neutral-300 dark:!border-input',
        'border-transparent peer-focus-visible:!border-r-0 !border-r-transparent dark:!border-r-transparent',
        'dark:hover:!bg-neutral-700',
      )}
      containerClass={cn('!rounded-lg !border-input dark:!bg-background')}
      country={'gb'}
      dropdownClass={cn('!text-neutral-900 !border-input :!bg-background dark:!text-neutral-300')}
      inputClass={cn(
        '!w-full py-5 !border-input !rounded-lg shadow-none !bg-background dark:!text-neutral-300',
        'focus-visible:!outline focus-visible:!outline-brand !outline-2 dark:focus-visible:!outline-white dark:focus-visible:!ring-2 peer',
      )}
      placeholder="Phone number"
      {...props}
    />
  )
}

// flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-brand
